import { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { CSSProperties } from 'react';
import { Text } from '@shared/components/Text';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { getPartialFormattedDateTimeRangeParts } from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getDateRangeText } from './util/getDateRangeText';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: DateRangeField;
}

/** Default and compact: the range, its ends joined by an en dash. Inline: as text. */
export function DateRangeCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={className}
    >
      {isInline && <InlineDateRange field={field} />}
      {isCompact && <FormattedDateRange field={field} />}
      {isDefault && <FormattedDateRange field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineDateRange({ field }: { field: DateRangeField }) {
  return <>{getDateRangeText(field)}</>;
}

export function FormattedDateRange({
  field,
  textStyle,
}: {
  field: DateRangeField;
  textStyle?: CSSProperties;
}) {
  const { start, end } = field.dateRange;
  const { from, to } = getPartialFormattedDateTimeRangeParts(start, end);
  return (
    <Text style={textStyle}>
      <FormattedDateRangePart
        text={from}
        placeholder={i18n('workflowTemplate.component.dateRange.startPlaceholder')}
        textStyle={textStyle}
      />
      <Text> – </Text>
      <FormattedDateRangePart
        text={to}
        placeholder={i18n('workflowTemplate.component.dateRange.endPlaceholder')}
        textStyle={textStyle}
      />
    </Text>
  );
}

function FormattedDateRangePart({
  text,
  placeholder,
  textStyle,
}: {
  text?: string;
  placeholder: string;
  textStyle?: CSSProperties;
}) {
  const isPlaceholder = isNil(text);
  return (
    <Text inactive={isPlaceholder} style={textStyle} nowrap>
      {text ?? placeholder}
    </Text>
  );
}
